import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es";
import "flatpickr/dist/flatpickr.min.css";

const TIPO_OPTIONS = [
  { value: "VISITA", label: "Visita" },
  { value: "REUNION", label: "Reunión" },
  { value: "TAREA", label: "Tarea" },
  { value: "OTRO", label: "Otro" },
];

const ESTADO_OPTIONS = [
  { value: "PENDIENTE", label: "Pendiente" },
  { value: "COMPLETADA", label: "Completada" },
  { value: "CANCELADA", label: "Cancelada" },
];

const initialFormState = {
  titulo: "",
  descripcion: "",
  tipo: "OTRO",
  estado: "PENDIENTE",
  fecha_hora_inicio: "",
  fecha_hora_fin: "",
  lugar: "",
  asignado_a: "",
};

const AgendaEventModal = ({
  show,
  onHide,
  onSubmit,
  submitting = false,
  canAssignStudent = false,
  estudiantes = [],
  loadingEstudiantes = false,
  eventData = null,
  initialDate = null,
  title = "Nuevo Evento",
}) => {
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const startPickerRef = useRef(null);
  const endPickerRef = useRef(null);

  // Inicializar formulario
  useEffect(() => {
    if (show) {
      if (eventData) {
        setForm({
          titulo: eventData.titulo || "",
          descripcion: eventData.descripcion || "",
          tipo: eventData.tipo || "OTRO",
          estado: eventData.estado || "PENDIENTE",
          fecha_hora_inicio: eventData.fecha_hora_inicio || "",
          fecha_hora_fin: eventData.fecha_hora_fin || "",
          lugar: eventData.lugar || "",
          asignado_a: eventData.asignado_a || "",
        });
      } else {
        setForm({
          ...initialFormState,
          fecha_hora_inicio: initialDate
            ? `${initialDate}T09:00:00`
            : "",
        });
      }
      setErrors({});
    }
  }, [show, eventData, initialDate]);

  // Inicializar flatpickr
  useEffect(() => {
    if (show && startDateRef.current) {
      startPickerRef.current = flatpickr(startDateRef.current, {
        enableTime: true,
        dateFormat: "Y-m-d\\TH:i:S",
        altInput: true,
        altFormat: "d/m/Y H:i",
        locale: Spanish,
        defaultDate: form.fecha_hora_inicio || undefined,
        onChange: ([date]) => {
          if (date) {
            const iso = toLocalISO(date);
            setForm((prev) => ({ ...prev, fecha_hora_inicio: iso }));
          }
        },
      });
    }

    if (show && endDateRef.current) {
      endPickerRef.current = flatpickr(endDateRef.current, {
        enableTime: true,
        dateFormat: "Y-m-d\\TH:i:S",
        altInput: true,
        altFormat: "d/m/Y H:i",
        locale: Spanish,
        defaultDate: form.fecha_hora_fin || undefined,
        onChange: ([date]) => {
          if (date) {
            const iso = toLocalISO(date);
            setForm((prev) => ({ ...prev, fecha_hora_fin: iso }));
          }
        },
      });
    }

    return () => {
      startPickerRef.current?.destroy();
      endPickerRef.current?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Actualizar flatpickr cuando cambia eventData
  useEffect(() => {
    if (show && eventData) {
      if (startPickerRef.current && eventData.fecha_hora_inicio) {
        startPickerRef.current.setDate(eventData.fecha_hora_inicio, false);
      }
      if (endPickerRef.current && eventData.fecha_hora_fin) {
        endPickerRef.current.setDate(eventData.fecha_hora_fin, false);
      }
    }
  }, [show, eventData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.titulo.trim()) errs.titulo = "El título es requerido";
    if (form.titulo.length > 255)
      errs.titulo = "Máximo 255 caracteres";
    if (!form.fecha_hora_inicio)
      errs.fecha_hora_inicio = "La fecha de inicio es requerida";
    if (
      form.fecha_hora_inicio &&
      form.fecha_hora_fin &&
      new Date(form.fecha_hora_fin) < new Date(form.fecha_hora_inicio)
    ) {
      errs.fecha_hora_fin =
        "La fecha de fin no puede ser anterior a la de inicio";
    }
    if (form.lugar && form.lugar.length > 255)
      errs.lugar = "Máximo 255 caracteres";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    // Construir payload limpio
    const payload = {
      titulo: form.titulo.trim(),
      tipo: form.tipo,
      estado: form.estado,
      fecha_hora_inicio: form.fecha_hora_inicio,
    };

    if (form.descripcion.trim()) payload.descripcion = form.descripcion.trim();
    if (form.fecha_hora_fin) payload.fecha_hora_fin = form.fecha_hora_fin;
    if (form.lugar.trim()) payload.lugar = form.lugar.trim();
    if (canAssignStudent && form.asignado_a) {
      payload.asignado_a = Number(form.asignado_a);
    }

    try {
      await onSubmit(payload);
    } catch {
      // Error handled by parent / useAgenda
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onHide();
      }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content radius-16 bg-base border-0 shadow-lg">
          {/* Header */}
          <div className="modal-header py-16 px-24 border-bottom">
            <h5 className="modal-title fw-semibold d-flex align-items-center gap-8">
              <Icon
                icon={eventData ? "mdi:calendar-edit" : "mdi:calendar-plus"}
                className="text-primary-600 text-xl"
              />
              {title}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onHide}
              disabled={submitting}
            />
          </div>

          {/* Body */}
          <div className="modal-body p-24">
            <form id="agendaEventForm" onSubmit={handleSubmit}>
              <div className="row">
                {/* Título */}
                <div className="col-12 mb-20">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                    Título <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="titulo"
                    className={`form-control radius-8 ${errors.titulo ? "is-invalid" : ""}`}
                    placeholder="Ej: Revisión de avances"
                    value={form.titulo}
                    onChange={handleChange}
                    maxLength={255}
                    autoFocus
                  />
                  {errors.titulo && (
                    <div className="invalid-feedback">{errors.titulo}</div>
                  )}
                </div>

                {/* Tipo y Estado */}
                <div className="col-md-6 mb-20">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                    Tipo
                  </label>
                  <select
                    name="tipo"
                    className="form-select radius-8"
                    value={form.tipo}
                    onChange={handleChange}
                  >
                    {TIPO_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6 mb-20">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                    Estado
                  </label>
                  <select
                    name="estado"
                    className="form-select radius-8"
                    value={form.estado}
                    onChange={handleChange}
                  >
                    {ESTADO_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Fechas */}
                <div className="col-md-6 mb-20">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                    Fecha y hora de inicio <span className="text-danger">*</span>
                  </label>
                  <div className="position-relative">
                    <input
                      ref={startDateRef}
                      type="text"
                      className={`form-control radius-8 bg-base ${errors.fecha_hora_inicio ? "is-invalid" : ""}`}
                      placeholder="Seleccionar fecha y hora"
                    />
                    <span className="position-absolute end-0 top-50 translate-middle-y me-12 line-height-1">
                      <Icon
                        icon="solar:calendar-linear"
                        className="icon text-lg text-secondary-light"
                      />
                    </span>
                    {errors.fecha_hora_inicio && (
                      <div className="invalid-feedback">
                        {errors.fecha_hora_inicio}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-6 mb-20">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                    Fecha y hora de fin
                  </label>
                  <div className="position-relative">
                    <input
                      ref={endDateRef}
                      type="text"
                      className={`form-control radius-8 bg-base ${errors.fecha_hora_fin ? "is-invalid" : ""}`}
                      placeholder="Seleccionar fecha y hora"
                    />
                    <span className="position-absolute end-0 top-50 translate-middle-y me-12 line-height-1">
                      <Icon
                        icon="solar:calendar-linear"
                        className="icon text-lg text-secondary-light"
                      />
                    </span>
                    {errors.fecha_hora_fin && (
                      <div className="invalid-feedback">
                        {errors.fecha_hora_fin}
                      </div>
                    )}
                  </div>
                </div>

                {/* Lugar */}
                <div className="col-12 mb-20">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                    Lugar
                  </label>
                  <input
                    type="text"
                    name="lugar"
                    className={`form-control radius-8 ${errors.lugar ? "is-invalid" : ""}`}
                    placeholder="Ej: Sala A, Oficina 3"
                    value={form.lugar}
                    onChange={handleChange}
                    maxLength={255}
                  />
                  {errors.lugar && (
                    <div className="invalid-feedback">{errors.lugar}</div>
                  )}
                </div>

                {/* Estudiante (solo mentores/superusuarios) */}
                {canAssignStudent && (
                  <div className="col-12 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Asignar estudiante
                      <span className="text-secondary-light text-xs ms-8 fw-normal">
                        (opcional)
                      </span>
                    </label>
                    {loadingEstudiantes ? (
                      <div className="d-flex align-items-center gap-8 text-secondary-light text-sm py-8">
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        />
                        Cargando estudiantes...
                      </div>
                    ) : (
                      <select
                        name="asignado_a"
                        className="form-select radius-8"
                        value={form.asignado_a}
                        onChange={handleChange}
                      >
                        <option value="">— Sin estudiante —</option>
                        {estudiantes.map((est) => (
                          <option key={est.id} value={est.id}>
                            {est.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {/* Descripción */}
                <div className="col-12 mb-20">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                    Descripción
                  </label>
                  <textarea
                    name="descripcion"
                    className="form-control radius-8"
                    rows={3}
                    placeholder="Notas o detalles del evento..."
                    value={form.descripcion}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="modal-footer px-24 py-16 border-top">
            <button
              type="button"
              className="btn btn-outline-secondary radius-8 px-20"
              onClick={onHide}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="agendaEventForm"
              className="btn btn-primary radius-8 px-20 d-flex align-items-center gap-6"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  />
                  Guardando...
                </>
              ) : (
                <>
                  <Icon icon="mdi:content-save-outline" className="text-lg" />
                  {eventData ? "Actualizar" : "Crear Evento"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function toLocalISO(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

export default AgendaEventModal;
